import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { InfoBox, LoadingIndicator } from "@/components";
import { FormRichTextInput } from "@/components/form/FormRichTextInput";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";

import { useErrorToast } from "@/hooks/useErrorToast";
import { useSuccessToast } from "@/hooks/useSuccessToast";
import { useUpdateSetJobInternalConfig } from "@/hooks/updateSets/useUpdateSetJobInternalConfig";
import { JobInternalConfig as JobInternalConfigType } from "@/types";

const rubricSchema = z.object({
  autoProfileRatingRubric: z.string().min(1),
});

export const EditRubricModal = ({
  jobInternalConfig,
  onClose,
  onRegenerateClick,
}: {
  jobInternalConfig: JobInternalConfigType;
  onClose: () => void;
  onRegenerateClick: () => void;
}) => {
  /* Database CRUD Operations */
  // Update
  const { mutate: updateSetJobInternalConfig, isPending } =
    useUpdateSetJobInternalConfig();

  /* Misc Hooks */
  const errorToast = useErrorToast();
  const successToast = useSuccessToast();

  const form = useForm({
    defaultValues: {
      autoProfileRatingRubric: jobInternalConfig.autoProfileRatingRubric ?? "",
    },
    resolver: zodResolver(rubricSchema),
  });

  /* Handlers */
  const handleSubmit = (data: z.infer<typeof rubricSchema>) => {
    updateSetJobInternalConfig(
      {
        id: jobInternalConfig.id,
        updateRequest: {
          autoProfileRatingRubric: data.autoProfileRatingRubric,
        },
      },
      {
        onSuccess: () => {
          successToast({
            description: "Rubric updated!",
          });
          onClose();
        },
        onError: () => {
          errorToast({
            description: "Failed to update rubric",
          });
        },
      },
    );
  };

  /* Render */
  const render = () => {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-h-[85vh] sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Rubric</DialogTitle>
            <DialogDescription>
              Make manual adjustments to the rubric text below.
            </DialogDescription>
          </DialogHeader>

          <InfoBox>
            <p>
              Human Input is only used during rubric generation. Editing it
              after the rubric has been generated will have no effect on the
              existing rubric.
            </p>
            <p className="mt-2">
              Need to regenerate the rubric entirely?{" "}
              <button
                className="cursor-pointer font-medium underline"
                onClick={onRegenerateClick}
                type="button"
              >
                Click here
              </button>
            </p>
          </InfoBox>

          <Form {...form}>
            <form className="flex flex-col gap-4">
              <FormRichTextInput
                editorHeight="40vh"
                fieldName="autoProfileRatingRubric"
                form={form}
                label="Rubric"
              />
              <DialogFooter>
                <Button onClick={onClose} type="button" variant="outline">
                  Cancel
                </Button>
                <Button
                  disabled={isPending}
                  onClick={() => {
                    void form.handleSubmit(handleSubmit)();
                  }}
                  type="button"
                >
                  {isPending && <LoadingIndicator />}
                  Save Changes
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    );
  };

  return render();
};
